/* eslint-disable @next/next/no-img-element */
import { Post } from "integrations/contentful"
import { Main } from "app/core/components/layout/Main"
import Footer from "app/splash/components/Footer"
import Header from "app/splash/components/Header"
import Head from "next/head"
import _ from "lodash"
import { HoverSkew } from "app/splash/components/HoverSkew"
import { STag } from "@superwall-me/sui"
import moment from "moment"
import { ColorBlur } from "app/splash/components/ColorBlur"
import tw from "tailwind-styled-components"
import React from "react"
import { RichText } from "app/contentful/RichText"
import BlogLayout from "app/core/layouts/Blog"
import { Pricing } from "app/splash/components/splash-sections/Pricing"
import { useSession } from "@blitzjs/auth"
import { ContactForm } from "app/contentful/ContactForm"
import { Reviews } from "app/splash/components/splash-sections/Reviews"
import { SignUpButton } from "app/splash/components/Main"

const Title = tw.h1`
text-3xl text-left md:text-center lg:text-5xl font-medium md:my-3 !leading-tight
`

const Subtitle = tw.h2`
text-xl text-left md:text-center font-light opacity-80
`

const BlogHeader = ({ post }: { post: Post }) => {
  return (
    <Head>
      <title>{post.title} — Superwall</title>
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Superwall" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:updated_time" content={moment(post.updatedAt).format()} />
      <meta property="article:published_time" content={moment(post.createdAt).format()} />
      <meta property="article:modified_time" content={moment(post.updatedAt).format()} />
      <meta property="article:section" content="Blog" />
      <meta property="article:tag" content={post.tags.join(", ")} />
      <meta property="og:title" content={post.title} />
      <meta property="og:description" content={post.subtitle} />
      <meta property="og:url" content={`https://www.superwall.com/blog/${post.slug}`} />
      {/* <meta property="og:image:width" content="1200" /> */}
      {/* <meta property="og:image:height" content="630" /> */}
      {post.image && <meta property="og:image:alt" content={post.image.title} />}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:title" content={post.title} />
      <meta property="twitter:description" content={post.subtitle} />
      {post.image && <meta property="twitter:image" content={`https:${post.image.url}`} />}
      <meta property="twitter:url" content={`https://www.superwall.com/blog/${post.slug}`} />
      <meta name="description" content={post.subtitle} />
      <meta name="keywords" content={post.tags.join(", ")} />
      {post.image && <meta property="og:image" content={`https:${post.image.url}`} />}
      <meta property="twitter:domain" content="superwall.com" />
    </Head>
  )
}

const BlogTag = ({ post }: { post: Pick<Post, "tags"> }) => {
  return (
    (post.tags[0] && (
      <span className="mx-2">
        <STag variant="primary">
          <span className="md:text-lg">{post.tags[0]}</span>
        </STag>
      </span>
    )) ||
    null
  )
}

const BlogPost = ({ post }: { post: Post }) => {
  const session = useSession({ suspense: false })

  return (
    <>
      <Header />
      <Main>
        <BlogHeader post={post} />
        {/* <ColorBlur /> */}
        <div className="h-12 md:h-24"></div>
        <div className="max-w-3xl mx-auto min-w-xl">
          <div className="text-center">
            <ColorBlur opacity={0.15} />
            <div className="flex flex-col gap-5 text-center items-center relative z-1">
              <div className="flex flex-col items-start md:items-center justify-center">
                <BlogTag post={post} />
                <div className="flex flex-col gap-2 pt-3 px-[9px]">
                  <Title>{post.title}</Title>
                  <Subtitle>{post.subtitle}</Subtitle>
                </div>
                <div className="">
                  {post.image ? (
                    <HoverSkew
                      glowStength="light"
                      clickable={true}
                      borderRadius="20px"
                      className="mt-6 md:mt-14 md:p-3 flex-shrink self-shrink hidden md:block"
                    >
                      <img
                        className="rounded-[11px] ring-1 ring-white/10 shadow-xl h-auto md:h-[30vw] xl:h-auto"
                        src={post.image.url}
                        alt={post.image.title}
                      />
                    </HoverSkew>
                  ) : null}
                  {post.image && (
                    <div className=" px-[9px]">
                      <img
                        className="block md:hidden mt-6 md:mt-14 shadow-xl relative rounded-lg "
                        style={{}}
                        src={post.image.url}
                        alt={post.image.title}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`h-[1px] w-[100%] my-6 md:my-14 ${
            post.image ? "" : ""
          } bg-gradient-to-r from-sky-300/0 via-purple-200/20 to-sky-300/0`}
        ></div>
        <div className="md:grid  md:grid-cols-[auto,1fr] lg:grid-cols-[1fr,auto,1fr]">
          <div className="hidden md:block pt-7 lg:p-7 lg:px-10">
            {/* <div className="float-right sticky top-20 md:top-24 ">
            {(post.sections || []).map((section, i) => {
              return <p key={i}>{section.name}</p>
            })}
          </div> */}
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="px-0  mb-5  md:mb-12">
              <div className="flex flex-row justify-between items-top px-[9px]">
                <div className="relative flex items-center">
                  <div>
                    {post.author && (
                      // eslint-disable-next-line jsx-a11y/alt-text
                      <img src={post.author?.image.url} className="w-12 h-12 rounded-xl mr-4" />
                    )}
                  </div>
                  <div className="">
                    <p className="font-semibold -mb-1 text-lg">{post.author?.name}</p>

                    <span className="opacity-50">{post.author?.title}</span>
                  </div>
                </div>
                <div className="text-right">
                  {post.updatedPostPublishDate ? (
                    <p className="font-semibold">
                      <time dateTime={post.updatedPostPublishDate.toString()} className="">
                        {moment(post.updatedPostPublishDate).format("MMMM D, YYYY")}
                      </time>
                    </p>
                  ) : (
                    <p className="font-semibold">
                      <time dateTime={post.createdAt.toString()} className="">
                        {moment(post.createdAt).format("MMMM D, YYYY")}
                      </time>
                    </p>
                  )}
                  {post.updatedPostPublishDate ? (
                    <span className="opacity-50">Updated</span>
                  ) : (
                    <span className="opacity-50">Published</span>
                  )}
                </div>
              </div>
            </div>
            {post.body ? (
              <RichText document={post.body} classNames="px-[9px]" />
            ) : (
              <p>No content found</p>
            )}
          </div>
        </div>
      </Main>
      <div className="h-6 md:h-4 lg:h-8"></div>
      {!session.userId && (
        <>
          <div
            className={`h-[1px] w-[100%] bg-gradient-to-r from-sky-300/0 via-purple-200/20 to-sky-300/0 mb-20`}
          ></div>

          <ContactForm
            page={{
              title: "Get a demo",
              subtitle: "We'd love to show you Superwall",
              slug: post.slug,
              section: "blog",
              body: (
                <div>
                  <h2 className="mt-0">Want to learn more?</h2>
                  <ol>
                    <li>Fill out this tiny form →</li>
                    <li>{`We'll prepare you a custom demo`}</li>
                    <li>{`Walk you through Superwall`}</li>
                    <li>{`Follow up and answer questions`}</li>
                  </ol>
                </div>
              ),
            }}
          ></ContactForm>
          <div className="mt-20 md:-mb-28 px-1">
            <Reviews title="Customer Stories">
              <div className="flex flex-row items-center gap-10 mb-16">
                <SignUpButton
                  source="main-cta"
                  mainCta="Start for free"
                  bookDemoCta={"Talk to sales"}
                  showBookDemo={true}
                ></SignUpButton>
              </div>
            </Reviews>
          </div>
          <div className="md:-mb-20 -mb-10 mt-32 px-4">
            <Pricing />
          </div>
        </>
      )}
      <Footer />
    </>
  )
}

BlogPost.getLayout = (page) => <BlogLayout>{page}</BlogLayout>

export default BlogPost
